/**
 * 起動中の表示（M8c-1）。マニフェスト取得から初回描画までの空白を埋める。
 *
 * 進捗が取れるとき（PDF モード）は割合を添え、取れないとき（画像モード）は
 * 不定形のスピナーだけを回す。見た目はテーマトークン（preloader-bg /
 * preloader-image / preloader-spinner）で差し替えられる。
 *
 * 支援技術には「読み込み中」を 1 度だけ伝える（role="status"）。割合の数字は
 * 更新のたびに読み上げられると煩いため aria-hidden にして視覚だけに出す。
 */
import { cls } from '../brand';
import type { Translate } from '../i18n/i18n';

export class Preloader {
  readonly el: HTMLElement;
  private readonly percent: HTMLElement;

  constructor(doc: Document, translate: Translate) {
    this.el = doc.createElement('div');
    this.el.className = cls('preloader');
    this.el.setAttribute('role', 'status');
    this.el.setAttribute('aria-live', 'polite');

    const spinner = doc.createElement('div');
    spinner.className = cls('preloader-spinner');
    spinner.setAttribute('aria-hidden', 'true');

    const label = doc.createElement('p');
    label.className = cls('preloader-label');
    label.textContent = translate('loading.label');

    this.percent = doc.createElement('span');
    this.percent.className = cls('preloader-percent');
    this.percent.setAttribute('aria-hidden', 'true');

    this.el.append(spinner, label, this.percent);
  }

  /** ルート直下へ設置する（描画面より手前）。 */
  mount(root: HTMLElement): void {
    root.appendChild(this.el);
    root.setAttribute('aria-busy', 'true');
  }

  /** 読み込みの割合（0–1）。取れない配信モードでは呼ばれない。 */
  setProgress(ratio: number): void {
    const clamped = Math.min(Math.max(ratio, 0), 1);
    this.percent.textContent = `${Math.round(clamped * 100)}%`;
  }

  /** 撤去する（ready でも error でも呼ぶ — 出したままにしない）。 */
  dispose(root: HTMLElement): void {
    this.el.remove();
    root.removeAttribute('aria-busy');
  }
}
